import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { ResPillComponent, ResPillStatus } from '../../../components/res-pill/res-pill.component';
import {
  kategoriePillFuer,
  PipeKategoriePillComponent,
} from '../../../components/pipe-kategorie-pill/pipe-kategorie-pill.component';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleaseResultsFilters, ReleaseRunResult, ReleaseRuns } from '../../../models/release-results.model';
import { TestRunLink, testRunExecutionsLink } from '../../../utils/test-run-routes';
import { PipelineFehlerKategorie } from '../../../models/test-run.model';
import { passtZurSuche, releaseResultsFilters } from '../test-results.utils';
import { ReleaseListState } from '../test-results.types';

interface RunsState {
  loading: boolean;
  error: boolean;
  data: ReleaseRuns | null;
}

interface RunsRequest {
  projectId: string;
  releaseId: string | null;
  filters: ReleaseResultsFilters;
  planKey: string | null;
  aktualisierung: number;
  versuch: number;
}

interface RunResult {
  pill: ResPillStatus;
  labelKey: string;
}

interface RunRow {
  lauf: ReleaseRunResult;
  link: TestRunLink;
  result: RunResult;
  /** Nur bei einem nicht aussagekraeftigen Lauf; er zeigt dann die Kategorie statt der Status-Pille. */
  kategorie: PipelineFehlerKategorie | null;
}

function runResult(lauf: ReleaseRunResult): RunResult {
  if (!lauf.ausgewertet) return { pill: 'not-run', labelKey: 'testResults.laeufe.status.notRun' };
  if (lauf.failed > 0) return { pill: 'failed', labelKey: 'testResults.laeufe.status.failed' };
  if (lauf.blocked > 0) return { pill: 'blocked', labelKey: 'testResults.laeufe.status.blocked' };
  return { pill: 'passed', labelKey: 'testResults.laeufe.status.passed' };
}

@Component({
  selector: 'app-lauf-ergebnis-tabelle',
  standalone: true,
  imports: [TranslatePipe, MatIcon, RouterLink, DatePipe, ResPillComponent, PipeKategoriePillComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './lauf-ergebnis-tabelle.component.html',
  styleUrl: './lauf-ergebnis-tabelle.component.scss',
})
export class LaufErgebnisTabelleComponent {
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly projectId = input.required<string>();
  readonly releaseId = input<string | null>(null);
  readonly releasesState = input<ReleaseListState>('loaded');
  readonly environment = input<string | null>(null);
  readonly planKey = input<string | null>(null);
  readonly suche = input('');
  /** Wechselt nach einem Import; die Daten werden dann neu geholt. */
  readonly aktualisierung = input(0);

  readonly alleLaeufe = output<void>();
  readonly releasesNeuLaden = output<void>();

  private readonly versuch = signal(0);

  protected readonly state = signal<RunsState>({ loading: false, error: false, data: null });

  protected readonly plan = computed(() => this.state().data?.plan ?? null);

  protected readonly keinLauf = computed(() => !this.state().data?.laeufe.length);

  protected readonly rows = computed<RunRow[]>(() =>
    (this.state().data?.laeufe ?? [])
      .filter((lauf) => passtZurSuche(this.suche(), [lauf.key, lauf.titel, lauf.planKey, lauf.umgebung]))
      .map((lauf) => ({
        lauf,
        link: testRunExecutionsLink(this.projectId(), lauf.id),
        result: runResult(lauf),
        kategorie: kategoriePillFuer(lauf),
      })),
  );

  private readonly request = computed<RunsRequest>(() => ({
    projectId: this.projectId(),
    releaseId: this.releaseId(),
    filters: releaseResultsFilters(this.environment()),
    planKey: this.planKey(),
    aktualisierung: this.aktualisierung(),
    versuch: this.versuch(),
  }));

  protected erneutLaden(): void {
    this.versuch.update((v) => v + 1);
  }

  constructor() {
    toObservable(this.request)
      .pipe(
        switchMap((req) => {
          if (!req.releaseId) return of<RunsState>({ loading: false, error: false, data: null });
          return this.resultsService.getRuns(req.projectId, req.releaseId, req.filters, req.planKey).pipe(
            map((data): RunsState => ({ loading: false, error: false, data })),
            catchError(() => of<RunsState>({ loading: false, error: true, data: null })),
            startWith<RunsState>({ loading: true, error: false, data: null }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.state.set(state));
  }

  /** Die Kategorie-Pill liegt fuer ihren Tooltip ueber dem Zeilen-Link und oeffnet den Lauf deshalb selbst. */
  protected openRun(link: TestRunLink): void {
    this.router.navigate(link.commands);
  }

  protected planLink(planId: string): string[] {
    return ['/projects', this.projectId(), 'test-plans', planId, 'edit'];
  }
}
